import { Button, Input, Modal, Row, Space, Typography } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { PlusOutlined, CompressOutlined, CloseCircleOutlined } from '@ant-design/icons';
import '../review-settings/reviewQuestions.less';
import { QueryBuilder } from '../../../../components/molecules/QueryBuilder/QueryBuilder';
import {
  DataTable,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { useConnection } from '../../../../connection/Application';
import Link from 'antd/lib/typography/Link';
import { useHistory } from 'react-router-dom';
const { Text } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;

export const UserGroupSets: FC = () => {
  const [questionSetModalVisible, setQuestionSetModalVisible] = useState<boolean>(false);
  const [name, setName] = useState<string>();
  const [query, setQuery] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [selectedUserGroup, setSelectedUserGroup] = useState<any>();
  const connection = useConnection();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [editable, setEditable] = useState<boolean>(false);
  const history = useHistory();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // eslint-disable-next-line react/display-name
      render: (name: Text, record: any) => (
        <Link
          onClick={() => {
            setSelectedUserGroup(record);
            setName(record.name);
            setQuestionSetModalVisible(true);
          }}>
          {name}
        </Link>
      ),
    },
  ];

  const search = useCallback(
    (query: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('user-group', { ...pagination, ...query })
        .then((value) => {
          setData(value.data);
          setTotal(value.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const createUserGroup = useCallback(() => {
    setFetching(true);
    connection
      .post('user-group', { userGroup: { name } })
      .then(() => {
        search(query, pagination);
        setName(undefined);
        setQuestionSetModalVisible(false);
        setFetching(false);
      })
      .catch((e) => {
        console.log(e);
        setFetching(false);
      });
  }, [connection, name, pagination, query, search]);

  const updateUserGroup = useCallback(() => {
    connection
      .patch(`user-group/${selectedUserGroup?.id}`, { userGroup: { name } })
      .then(() => {
        search(query, pagination);
        setName(undefined);
        setEditable(false);
        setQuestionSetModalVisible(false);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [connection, name, pagination, query, search, selectedUserGroup?.id]);

  useEffect(() => {
    search({}, { page: 0, size: 10 });
  }, [search]);

  const deleteUserGroup = useCallback(() => {
    connection
      .delete(`user-group/${selectedUserGroup?.id}`)
      .then(() => {
        setEditable(false);
        setSelectedUserGroup(undefined);
        setQuestionSetModalVisible(false);
        search(query, pagination);
      })
      .catch((e) => console.log(e));
  }, [connection, pagination, query, search, selectedUserGroup?.id]);

  const showDeleteConfirm = useCallback(() => {
    confirm({
      title: `Deleting User Group !`,
      icon: <CloseCircleOutlined />,
      content: `Are you sure delete this User Group?`,
      okText: 'Yes,Delete!',
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        deleteUserGroup();
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [deleteUserGroup]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="User Group"
        subTitle="Manage User Group details"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: '1%',
              width: '100%',
            }}>
            <QueryBuilder
              searchPlaceholder="Search Review Set"
              search={true}
              value={query}
              onChange={setQuery}
              onSearch={(value) => search(value, pagination)}
              schema={[]}
            />
            <Space>
              <Button
                onClick={() => {
                  setQuery(undefined);
                  search(undefined, { page: 1, size: pagination.size });
                }}>
                Reset
              </Button>
              <Button type="primary" onClick={() => search(query, pagination)}>
                Search
              </Button>
            </Space>
          </div>
        </WebUxPageItem>
        <WebUxPageItem>
          <Row
            style={{
              alignItems: 'baseline',
              marginTop: '1%',
              justifyContent: 'flex-end',
              marginBottom: '0.5%',
            }}>
            <Button
              type="primary"
              onClick={() => {
                setQuestionSetModalVisible(true);
              }}>
              <PlusOutlined style={{ fontSize: '100%', color: 'white' }} />
              New User Group
            </Button>
          </Row>
          <DataTable
            columns={columns}
            total={total}
            loading={loading}
            dataSource={data}
            onPaginationChange={(p) => {
              setPagination(p);
              if (pagination.page !== p.page || pagination.size !== p.size) {
                search(query, p);
              }
            }}
            dtPagination={pagination}
            style={{ marginTop: '1%' }}
          />
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        title={selectedUserGroup ? name : 'Add a User Group'}
        destroyOnClose
        footer={
          selectedUserGroup ? (
            <Row style={{ justifyContent: 'space-between', margin: '0 1% 0 1%' }}>
              <Button onClick={() => showDeleteConfirm()} danger>
                Delete
              </Button>
              <Space>
                <Button
                  type="default"
                  onClick={() =>
                    history.push(
                      `/webux/user-group/${selectedUserGroup?.id}/${encodeURIComponent(
                        name ?? '',
                      )}`,
                    )
                  }>
                  EditMembers
                </Button>
                {editable ? (
                  <>
                    <Button type="default" onClick={() => setEditable(false)}>
                      Cancel
                    </Button>
                    <Button type="primary" onClick={() => updateUserGroup()}>
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="default" onClick={() => setEditable(true)}>
                      Edit
                    </Button>
                    <Button
                      type="default"
                      onClick={() => {
                        setEditable(false);
                        setSelectedUserGroup(undefined);
                        setQuestionSetModalVisible(false);
                      }}>
                      Close
                    </Button>
                  </>
                )}
              </Space>
            </Row>
          ) : (
            <>
              <Button type="default" onClick={() => setEditable(false)}>
                Cancel
              </Button>
              <Button type="primary" loading={fetching} onClick={() => createUserGroup()}>
                Save
              </Button>
            </>
          )
        }
        open={questionSetModalVisible}
        onCancel={() => {
          setSelectedUserGroup(undefined);
          setEditable(false);
          setName(undefined);
          setQuestionSetModalVisible(false);
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Space>
            <Space style={{ width: 100 }}>
              <CompressOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Name</Text>
            </Space>
            <div style={{ width: 400 }}>
              {!editable && selectedUserGroup ? (
                <Text>{name}</Text>
              ) : (
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  allowClear
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              )}
            </div>
          </Space>
        </div>
      </Modal>
    </WebUxPage>
  );
};
